import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { LinearProgress, Tooltip as MuiTooltip, Popper,Grid, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,Modal, Dialog, DialogTitle,DialogContent, IconButton , DialogActions, Button, SpeedDial, SpeedDialAction, } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { transformDualAxisData } from './transformDualAxisData';
import ExportMenuGeneric from './ExportMenuGeneric';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

Chart.register(zoomPlugin);

const t = (translate, key, fallback) => {
  const value = typeof translate === 'function' ? translate(key) : key;
  return value && value !== key ? value : fallback;
};

const Leyendas = ({ data, toggle, visible, width ='29vw', translate }) => {
  const [legendItems, setLegendItems] = useState([]);

  useEffect(() => {
    const groups = Object.values(
      data.datasets.reduce((acc, ds, idx) => {
        const key = ds.entityKey ?? ds.label;
        if (!acc[key]) acc[key] = { key, label: ds.label, backgroundColor: ds.backgroundColor, indices: [idx] };
        else acc[key].indices.push(idx);
        return acc;
      }, {})
    );

    const items = groups.map(group => {
      const hasRealData = group.indices.some(i =>
        Array.isArray(data.datasets[i].data) &&
        data.datasets[i].data.some(v => v != null && v !== 0)
      );
      const zeroOnly = !hasRealData && group.indices.some(i =>
        Array.isArray(data.datasets[i].data) &&
        data.datasets[i].data.some(v => v === 0)
      );
      const isVisible = group.indices.every(i => visible[i]);
      return { ...group, hasRealData, zeroOnly, isVisible };
    });

    items.sort((a, b) => {
      if (a.hasRealData !== b.hasRealData) return a.hasRealData ? -1 : 1;
      if (a.zeroOnly !== b.zeroOnly) return a.zeroOnly ? -1 : 1;
      return 0;
    });

    setLegendItems(items);
  }, [data.datasets, visible]);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', maxHeight: '5vh', minHeight: '5vh', width: width, overflowY: 'auto' }}>
      {legendItems.map(({ key, label, backgroundColor, indices, hasRealData, zeroOnly, isVisible }) => {
        const node = (
          <div
            key={key}
            onClick={() => hasRealData && indices.forEach(i => toggle(i))}
            style={{
              position: 'relative',
              display: 'flex', alignItems: 'center',
              height: '2vh', marginRight: '0.4vw',
              cursor: hasRealData ? 'pointer' : 'default',
              opacity: (hasRealData || zeroOnly) ? 1 : 0.5,
            }}
          >
            <div style={{ backgroundColor, width: '1vw', height: '0.4vw', marginRight: '0.1vw' }} />
            <p style={{ textDecoration: hasRealData && isVisible ? 'none' : 'line-through', color: '#777', fontSize: '0.6vw' }}>
              {label}
            </p>
          </div>
        );

        if (hasRealData) return node;
        if (zeroOnly) return <MuiTooltip key={key} title={t(translate, 'zero_value', 'Valor 0')}>{node}</MuiTooltip>;
        return <MuiTooltip key={key} title={t(translate, 'no_data', 'No hay datos')}>{node}</MuiTooltip>;
      })}
    </div>
  );
};

// ----------------- Tooltip Popper Body -----------------
function TooltipPivot({ title, axisTitles, rows, titulo, translate }) {
  return (
    <div style={{ maxWidth: '40vw' }}>
      {title ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#a1a1a1', textAlign: 'center', mb: '.5vh', fontSize:'.85vw' }}>
          {title}
        </Typography>
      ) : null}

      <div style={{ maxHeight: '30vh',  overflowY: 'auto',  }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: '.2vw', fontSize: '0.7vw', color: '#a1a1a1' }}>
                {rows.isFecha ? t(translate, 'dates', 'Fechas') : titulo}
              </TableCell>
              {axisTitles.map(a => (
                <TableCell key={a.id} align="center" sx={{ py: '1vh', fontSize: '0.7vw', color: a.color, fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {a.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody >
            {rows.order.map(lbl => {
              const perAxis = rows.matrix.get(lbl) || {};
              const firstAxisId = axisTitles[0]?.id;
              const labelColor = perAxis[firstAxisId]?.color || '#a1a1a1';

              return (
                <TableRow key={lbl} style={{border:'solid 0.01vh'}}>
                  <TableCell sx={{ py: 0.5, fontSize: '0.7vw', color: labelColor, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {lbl}
                  </TableCell>
                  {axisTitles.map(a => {
                    const cell = perAxis[a.id];
                    const raw = cell?.value;
                    let txt;
                    if (raw === null || raw === undefined || raw === '') {
                      txt = '--';
                    } else {
                      txt = (typeof raw === 'number') ? (Number.isInteger(raw) ? raw : Number(raw).toFixed(2)) : raw;
                    }
                    return (
                      <TableCell key={a.id} align="center" sx={{ py: 0.5 }}>
                        <span style={{ color: '#a1a1a1', fontWeight: 700,fontSize: '0.7vw', }}>{txt}</span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


const BarChartGeneric = ({
  chartData, cardStyles, alarmGraph, handleContextMenu,
  toggleLabels, styleCardComparar, titulo,
  nivelSeleccionado, colorManual = false, jaulaSeleccionada,
  marginRight, marginLeft, iconLeft, invertVS=true, translate,
  selectedDay, viewMode,
}) => {
  const fontSizeEjes = window.innerWidth * 0.0065;
  const [progressPDF, setProgressPDF] = useState(0);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const MONTHS_ES = {
    enero: 0,
    febrero: 1,
    marzo: 2,
    abril: 3,
    mayo: 4,
    junio: 5,
    julio: 6,
    agosto: 7,
    septiembre: 8,
    octubre: 9,
    noviembre: 10,
    diciembre: 11,
  };

  // === Helpers de ordenamiento ===
  const parseTemporalLabel = (label) => {
    const normalizedLabel = String(label ?? '').trim();

    // dd/MM/yyyy o dd/MM/yyyy HH:mm[:ss]
    const dateMatch = normalizedLabel.match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (dateMatch) {
      const [, dd, mm, yyyy, hh = '00', mi = '00', ss = '00'] = dateMatch;
      const d = new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(mi),
        Number(ss)
      );
      return { kind: 'day', sortValue: d.getTime() };
    }

    // Semana N o Semana N YYYY
    const weekMatch = normalizedLabel.match(/^Semana\s+(\d{1,2})(?:\s+(\d{4}))?$/i);
    if (weekMatch) {
      const [, week, year] = weekMatch;
      const sortValue = year
        ? (Number(year) * 100) + Number(week)
        : Number(week);
      return { kind: 'week', sortValue };
    }

    // Mes YYYY
    const monthMatch = normalizedLabel.match(/^([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s+(\d{4})$/);
    if (monthMatch) {
      const [, monthNameRaw, year] = monthMatch;
      const monthIndex = MONTHS_ES[monthNameRaw.toLowerCase()];
      if (monthIndex !== undefined) {
        return {
          kind: 'month',
          sortValue: new Date(Number(year), monthIndex, 1).getTime(),
        };
      }
    }

    return { kind: 'text', sortValue: NaN };
  };

  const formatTemporalTickLabel = (label, isInverted) => {
    const labelStr = String(label ?? '').trim();

    if (isInverted) {
      if (labelStr.toUpperCase().startsWith('JAULA')) {
        const [first, ...rest] = labelStr.split(' ');
        return [first, rest.join(' ')];
      }
      return labelStr;
    }

    const parsed = parseTemporalLabel(labelStr);
    if (parsed.kind === 'day') {
      const [day, month, year] = labelStr.split('/');
      return [`${day}/${month}`, year];
    }

    if (parsed.kind === 'week') {
      const weekMatch = labelStr.match(/^Semana\s+(\d{1,2})(?:\s+(\d{4}))?$/i);
      if (!weekMatch) return labelStr;
      const [, week, year] = weekMatch;
      return year ? [`${translate('week')} ${week}`, year] : `${translate('week')} ${week}`;
    }

    if (parsed.kind === 'month') {
      const monthMatch = labelStr.match(/^([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s+(\d{4})$/);
      if (!monthMatch) return labelStr;
      const [, monthName, year] = monthMatch;
      return [monthName, year];
    }

    return labelStr;
  };

  const sortChartDataByLabels = (data, { labelsAreDates, order = 'asc', sortKeys = data?.labels } = {}) => {
      if (!data || !Array.isArray(data.labels)) return data;

      const { labels, datasets } = data;
      const idx = labels.map((_, i) => i); // índice estable

      const cmpAsc = (i, j) => {
        const a = sortKeys[i], b = sortKeys[j];
        if (labelsAreDates) {
          const ta = parseTemporalLabel(a).sortValue;
          const tb = parseTemporalLabel(b).sortValue;
          if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
          // fallback natural por si llega un label raro
          return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
        } else {
          // "Jaula 2" < "Jaula 10" (orden natural)
          return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
        }
      };

      idx.sort((i, j) => {
        const c = cmpAsc(i, j);
        if (c !== 0) return order === 'asc' ? c : -c;
        return i - j; // estable
      });

      const remap = (arr) => idx.map(k => arr[k]);
      return {
        labels: remap(labels),
        datasets: (datasets || []).map(ds => ({
          ...ds,
          data: remap(Array.isArray(ds.data) ? ds.data : []),
        })),
      };
  };

  const filteredBySelectedDay = useMemo(() => {
    const safeChartData = Array.isArray(chartData) ? chartData : [];

    if (viewMode !== 'day' || !selectedDay) {
      return safeChartData;
    }

    return safeChartData.map((entry) => {
      const summary = Array.isArray(entry?.values_summary?.[0])
        ? entry.values_summary[0]
        : entry?.values_summary;
      const safeSummary = Array.isArray(summary) ? summary : [];

      return {
        ...entry,
        values_summary: safeSummary.filter((item) => item?.day === selectedDay),
      };
    });
  }, [chartData, selectedDay, viewMode]);

  // Filtrar por jaula
  const usedChartData = jaulaSeleccionada
    ? filteredBySelectedDay.filter(j => j.name === jaulaSeleccionada)
    : filteredBySelectedDay;

  // Días comunes según nivel
  let commonDays;
  if (nivelSeleccionado === 'area' || nivelSeleccionado === 'region') {
    const summaryObj = usedChartData?.[0]?.values_summary;
    const summaryArray = summaryObj && typeof summaryObj === 'object'
      ? Object.values(summaryObj)
      : [];
    commonDays = summaryArray.map(item => item.day);
  } else if (nivelSeleccionado === 'centro') {
    const singleChart = usedChartData?.[0];
    const summaryArray = Array.isArray(singleChart?.values_summary?.[0])
      ? singleChart?.values_summary[0]
      : singleChart?.values_summary;
    commonDays = summaryArray?.map(item => item.day) ?? [];
  } else {
    commonDays = [];
  }

  const [invert, setInvert] = useState(invertVS ? true : false);

  // selección dual-axis
  const initialSelection = toggleLabels.reduce((acc, item) => {
    acc[item.field] = true;
    return acc;
  }, {});
  const [dualAxisSelection, setDualAxisSelection] = useState(initialSelection);

  // Data
  const datos = transformDualAxisData(usedChartData, invert, dualAxisSelection, commonDays, alarmGraph);

  // Data ORDENADA para el chart
  const sortedDatos = useMemo(() => {
    // Cuando invert === false → X son FECHAS ⇒ ordénalas cronológicamente
    // Cuando invert === true  → X son JAULAS ⇒ orden natural (Jaula 1, Jaula 2, ...)
    const labelsAreDates = !invert;
    // Si quisieras descendente en algún caso, cambia order: 'desc'
    return sortChartDataByLabels(datos, { labelsAreDates, order: 'asc', sortKeys: invert ? usedChartData.map(j => j.name) : datos.labels });
  }, [datos, invert, usedChartData]);

  // visibilidad datasets
  const [datasetsVisibility, setDatasetsVisibility] = useState(datos.datasets.map(() => true));
  
  // REFS
  const modalChartRef = useRef(null);
  const expandedCanvasRef = useRef(null);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const graceTimerRef = useRef(null);

  const labelsLen = Array.isArray(datos.labels) ? datos.labels.length : 0;
  const originalXMin = 0;
  const originalXMax = Math.max(0, labelsLen - 1);
  const originalXRef = useRef({ min: originalXMin, max: originalXMax });
  originalXRef.current = { min: originalXMin, max: originalXMax };

  const toggleDatasetVisibility = (idx) => {
    const chart = chartInstanceRef.current;
    if (!chart) return;
    const willShow = !chart.isDatasetVisible(idx);
    chart.setDatasetVisibility(idx, willShow);
    setDatasetsVisibility(v => {
      const nv = [...v]; nv[idx] = willShow; return nv;
    });
    chart.update('none');
  };
  const toggleDatasetVisibilityModal = (idx) => {
    const chart = expandedCanvasRef.current;
    if (!chart) return;
    const willShow = !chart.isDatasetVisible(idx);
    chart.setDatasetVisibility(idx, willShow);
    setDatasetsVisibility(v => {
      const nv = [...v]; nv[idx] = willShow; return nv;
    });
    chart.update('none');
  };

  const forceClose = (chart) => {
    // respeta lock y freeze
    if (tooltipLockedRef.current || chart?._tooltipFrozen) return;

    setPopperOpen(false);
    popperOpenRef.current = false;

    try {
      chart?.tooltip?.setActiveElements?.([], { x: 0, y: 0 });
      chart?.setActiveElements?.([]);
      chart?.update?.('none');
    } catch {}
  };

  const dynamicYScales = toggleLabels.reduce((acc, { field, label, activeColor, inactiveColor, axis }) => {
    acc[`y-${axis}`] = {
      type: 'linear',
      position: axis,
      title: { display: false, text: [label] },
      ticks: {
        font: { size: fontSizeEjes },
        color: dualAxisSelection[field] ? activeColor : inactiveColor,
      },
      grid: {
        color: axis === 'left'
          ? 'rgba(255, 255, 255, 0)'
          : 'rgba(126, 121, 121, 0.38)',
        lineWidth: 0.2,
      },
      beginAtZero: true,
    };
    return acc;
  }, {});
  // Tamaños para el canvas según modo
  const expandedChartBoxStyle = { height: '43vh', width: '79vw', padding: '1vh 1vw' };
  // ----------------- MUI Popper Tooltip State/Refs -----------------
  const virtualAnchorRef = useRef({ getBoundingClientRect: () => new DOMRect(), contextElement: null });
  const popperOpenRef = useRef(false);
  // const [popperOpen, setPopperOpen] = useState(false);
  const [popperOpen, setPopperOpen] = useState(false);
  const [popperPlacement, setPopperPlacement] = useState('top'); // 'top' | 'bottom'

  const [popperData, setPopperData] = useState({ title: '', axisTitles: [], rows: { order: [], matrix: new Map(), isFecha: false } });
  const hideTimerRef = useRef(null);
  const hideScheduledRef = useRef(false);
  const tooltipLockedRef = useRef(false);

  const leftAxisPatternPlugin = {
    id: 'leftAxisPattern',

    // ⚙️ Parámetros ajustables en vw
    lineSpacing: 0.05,     // 1vw ≈ 19px en 1920px de ancho → separaciones amplias
    lineAngle: 45,        // ángulo en grados
    lineWidth: 0.05,      // grosor de las líneas (0.15vw ≈ 3px)
    borderWidth: 0.05,     // grosor del contorno (0.2vw ≈ 4px)
    crossPattern: false,   // patrón cruzado ↘ + ↙

    afterDatasetsDraw(chart) {
      const { ctx, chartArea } = chart;
      const vw = chartArea.width / window.innerWidth * 100; // escala proporcional

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const yAxis = chart.getDatasetMeta(datasetIndex).yAxisID;
        if (yAxis !== 'y-left') return;

        const meta = chart.getDatasetMeta(datasetIndex);
        // Si el dataset no está visible, saltarlo
        if (meta.hidden || !chart.isDatasetVisible(datasetIndex)) return;
        //  Solo aplicar patrón a las barras visibles del eje izquierdo
        if (meta.yAxisID !== 'y-left') return;
        
        meta.data.forEach((bar, i) => {
          const { x, y, base } = bar;
          const barWidth = bar.width;
          const barHeight = base - y;

          // === Color base ===
          let baseColor = dataset.backgroundColor;
          if (Array.isArray(baseColor)) baseColor = baseColor[i] || '#999';

          const rgbaMatch = baseColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          let r = 150, g = 150, b = 150;
          if (rgbaMatch) [r, g, b] = rgbaMatch.slice(1, 4).map(Number);

          const fillColor = baseColor;
          const solidColor = `rgb(${r}, ${g}, ${b})`;

          // === Fondo ===
          ctx.fillStyle = fillColor;
          ctx.fillRect(x - barWidth / 2, y, barWidth, barHeight);

          // === Líneas diagonales ===
          ctx.save();
          ctx.beginPath();
          ctx.rect(x - barWidth / 2, y, barWidth, barHeight);
          ctx.clip();

          ctx.strokeStyle = solidColor;
          ctx.lineWidth = leftAxisPatternPlugin.lineWidth * vw;

          const spacing = leftAxisPatternPlugin.lineSpacing * vw * 10; // escala con viewport
          const angle = (Math.PI / 180) * leftAxisPatternPlugin.lineAngle;
          const tan = Math.tan(angle);
          const diagLength = Math.sqrt(barWidth ** 2 + barHeight ** 2);
          const extra = diagLength * Math.sin(angle);

          // ↘ líneas diagonales
          for (let offset = -barHeight - extra; offset < barWidth + extra; offset += spacing) {
            ctx.beginPath();
            ctx.moveTo(x - barWidth / 2 + offset, y);
            ctx.lineTo(x - barWidth / 2 + offset + barHeight / tan, y + barHeight);
            ctx.stroke();
          }

          // ↙ cruzadas (opcional)
          if (leftAxisPatternPlugin.crossPattern) {
            for (let offset = -barHeight - extra; offset < barWidth + extra; offset += spacing) {
              ctx.beginPath();
              ctx.moveTo(x + barWidth / 2 - offset, y);
              ctx.lineTo(x + barWidth / 2 - offset - barHeight / tan, y + barHeight);
              ctx.stroke();
            }
          }

          ctx.restore();

          // === Borde exterior ===
          ctx.save();
          ctx.lineWidth = leftAxisPatternPlugin.borderWidth * vw;
          ctx.strokeStyle = solidColor;
          ctx.strokeRect(x - barWidth / 2, y, barWidth, barHeight);
          ctx.restore();
        });
      });
    },
  };
  // modal de descargar pdf con opciones
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleOpenDialog = () => setDialogOpen(true);

  // === Estado para expansión en modal ===
  const [expanded, setExpanded] = useState(false);

  // const leftAxisPatternPlugin = {
  //   id: 'leftAxisPattern',

  //   // ⚙️ Parámetros ajustables
  //   lineSpacing: 20,       // espacio entre líneas
  //   lineAngle: 45,         // ángulo en grados
  //   lineWidth: 3,        // grosor de las líneas
  //   borderWidth: 3,        // grosor del contorno
  //   crossPattern: true,   // activa/desactiva patrón cruzado

  //   afterDatasetsDraw(chart) {
  //     const { ctx } = chart;

  //     chart.data.datasets.forEach((dataset, datasetIndex) => {
  //       const yAxis = chart.getDatasetMeta(datasetIndex).yAxisID;
  //       if (yAxis !== 'y-left') return; // aplica solo a eje izquierdo

  //       const meta = chart.getDatasetMeta(datasetIndex);

  //       meta.data.forEach((bar, i) => {
  //         const { x, y, base } = bar;
  //         const barWidth = bar.width;
  //         const barHeight = base - y;

  //         // === Color base tal cual llega ===
  //         let baseColor = dataset.backgroundColor;
  //         if (Array.isArray(baseColor)) baseColor = baseColor[i] || '#999';

  //         // Extraer solo el RGB (sin alpha)
  //         const rgbaMatch = baseColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  //         let r = 150, g = 150, b = 150;
  //         if (rgbaMatch) [r, g, b] = rgbaMatch.slice(1, 4).map(Number);

  //         const fillColor = baseColor;              // usa color original (RGBA)
  //         const solidColor = `rgb(${r}, ${g}, ${b})`; // color sin transparencia

  //         // === 1. Fondo ===
  //         ctx.fillStyle = fillColor;
  //         ctx.fillRect(x - barWidth / 2, y, barWidth, barHeight);

  //         // === 2. Líneas diagonales ===
  //         ctx.save();
  //         ctx.beginPath();
  //         ctx.rect(x - barWidth / 2, y, barWidth, barHeight);
  //         ctx.clip();

  //         ctx.strokeStyle = solidColor;
  //         ctx.lineWidth = leftAxisPatternPlugin.lineWidth;

  //         const spacing = leftAxisPatternPlugin.lineSpacing;
  //         const angle = (Math.PI / 180) * leftAxisPatternPlugin.lineAngle;
  //         const tan = Math.tan(angle);

  //         const diagLength = Math.sqrt(barWidth ** 2 + barHeight ** 2);
  //         const extra = diagLength * Math.sin(angle);

  //         // ↘ Líneas diagonales principales
  //         for (let offset = -barHeight - extra; offset < barWidth + extra; offset += spacing) {
  //           ctx.beginPath();
  //           ctx.moveTo(x - barWidth / 2 + offset, y);
  //           ctx.lineTo(x - barWidth / 2 + offset + barHeight / tan, y + barHeight);
  //           ctx.stroke();
  //         }

  //         // ↙ Líneas cruzadas (opcional)
  //         if (leftAxisPatternPlugin.crossPattern) {
  //           for (let offset = -barHeight - extra; offset < barWidth + extra; offset += spacing) {
  //             ctx.beginPath();
  //             ctx.moveTo(x + barWidth / 2 - offset, y);
  //             ctx.lineTo(x + barWidth / 2 - offset - barHeight / tan, y + barHeight);
  //             ctx.stroke();
  //           }
  //         }

  //         ctx.restore();

  //         // === 3. Borde de la barra ===
  //         ctx.save();
  //         ctx.lineWidth = leftAxisPatternPlugin.borderWidth;
  //         ctx.strokeStyle = solidColor;
  //         ctx.strokeRect(x - barWidth / 2, y, barWidth, barHeight);
  //         ctx.restore();
  //       });
  //     });
  //   },
  // };



// const leftAxisPatternPlugin = {
//   id: 'leftAxisPattern',

//   // ⚙️ Parámetros ajustables
//   lineTransparency: 1.0,   // líneas sólidas
//   lineSpacing: 10,         // espacio entre líneas (más alto = más separación)
//   lineAngle: 45,           // ángulo diagonal
//   crossPattern: false,     // si quieres activar líneas cruzadas
//   lineWidth: 2,            // grosor de las líneas
//   borderWidth: 2,          // grosor del borde exterior

//   afterDatasetsDraw(chart) {
//     const { ctx } = chart;

//     chart.data.datasets.forEach((dataset, datasetIndex) => {
//       const yAxis = chart.getDatasetMeta(datasetIndex).yAxisID;
//       if (yAxis !== 'y-left') return; // solo eje izquierdo

//       const meta = chart.getDatasetMeta(datasetIndex);

//       meta.data.forEach((bar, i) => {
//         const { x, y, base } = bar;
//         const barWidth = bar.width;
//         const barHeight = base - y;

//         // === Color base RGBA ===
//         let baseColor = dataset.backgroundColor;
//         if (Array.isArray(baseColor)) baseColor = baseColor[i] || '#999';

//         // Extraer solo el RGB (sin alpha)
//         const rgbaMatch = baseColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
//         let r = 150, g = 150, b = 150;
//         if (rgbaMatch) [r, g, b] = rgbaMatch.slice(1, 4).map(Number);

//         const fillColor = baseColor; // mantiene transparencia original
//         const solidColor = `rgb(${r}, ${g}, ${b})`; // color sólido base

//         // === Relleno translúcido ===
//         ctx.fillStyle = fillColor;
//         ctx.fillRect(x - barWidth / 2, y, barWidth, barHeight);

//         // === Patrón de líneas ===
//         ctx.save();
//         ctx.beginPath();
//         ctx.rect(x - barWidth / 2, y, barWidth, barHeight);
//         ctx.clip();

//         ctx.strokeStyle = solidColor;
//         ctx.lineWidth = leftAxisPatternPlugin.lineWidth;

//         const spacing = leftAxisPatternPlugin.lineSpacing;
//         const angle = (Math.PI / 180) * leftAxisPatternPlugin.lineAngle;
//         const tan = Math.tan(angle);

//         // Asegura que cubra toda la barra
//         const diagLength = Math.sqrt(barWidth ** 2 + barHeight ** 2);
//         const extra = diagLength * Math.sin(angle);

//         // ↘ Líneas diagonales principales
//         for (let offset = -barHeight - extra; offset < barWidth + extra; offset += spacing) {
//           ctx.beginPath();
//           ctx.moveTo(x - barWidth / 2 + offset, y);
//           ctx.lineTo(x - barWidth / 2 + offset + barHeight / tan, y + barHeight);
//           ctx.stroke();
//         }

//         // ↙ Líneas cruzadas (si se activa)
//         if (leftAxisPatternPlugin.crossPattern) {
//           for (let offset = -barHeight - extra; offset < barWidth + extra; offset += spacing) {
//             ctx.beginPath();
//             ctx.moveTo(x + barWidth / 2 - offset, y);
//             ctx.lineTo(x + barWidth / 2 - offset - barHeight / tan, y + barHeight);
//             ctx.stroke();
//           }
//         }

//         ctx.restore();

//         // === Borde del contorno de la barra ===
//         ctx.save();
//         ctx.lineWidth = leftAxisPatternPlugin.borderWidth;
//         ctx.strokeStyle = solidColor;
//         ctx.strokeRect(x - barWidth / 2, y, barWidth, barHeight);
//         ctx.restore();
//       });
//     });
//   },
// };



  // Opciones con zoom
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { rectangle: { minBarLength: 20 } },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      tooltip: {
        enabled: false, // usamos Popper
        external: (context) => {
          if (typeof window !== 'undefined') {
            if (externalPopperTooltip.raf) cancelAnimationFrame(externalPopperTooltip.raf);
            externalPopperTooltip.raf = requestAnimationFrame(() => externalPopperTooltip(context));
          } else {
            externalPopperTooltip(context);
          }
        },
        mode: 'index',
        intersect: false,
        displayColors: false,
      },
      legend: { display: false },
      zoom: {
        limits: { x: { min: originalXRef.current.min, max: originalXRef.current.max } },
        pan: { enabled: true, mode: 'x', modifierKey: null },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          drag: { enabled: true, modifierKey: 'ctrl' },
          mode: 'x',
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: invert ? titulo : t(translate, 'dates', 'Fechas') },
        min: originalXRef.current.min,
        max: originalXRef.current.max,
        ticks: {
          font: { size: fontSizeEjes },
          color: '#C3C3C3',
          callback: function (value) {
            const raw = this.getLabelForValue(value);
            return formatTemporalTickLabel(raw, invert);
          }
        },
        grid: { color: 'rgba(126, 121, 121, 0.38)', lineWidth: 0.2 },
      },
      ...dynamicYScales,
    },
    indexAxis: 'x',
  };

  // Crear/actualizar chart
  useEffect(() => {
    if (!chartRef.current) return;
    // registrar plugin personalizado (solo una vez)
    // Chart.register(leftAxisPatternPlugin);
    // const instance = new Chart(chartRef.current, { type: 'bar', data: sortedDatos, options: barOptions });
    // 

    const instance = new Chart(chartRef.current, {
      type: 'bar',
      data: sortedDatos,
      options: barOptions,
      plugins: [leftAxisPatternPlugin], 
    });

    chartInstanceRef.current = instance;

    // listeners auxiliares
    const canvas = chartRef.current;
    const handleMouseMove = (e) => {
      instance._tooltipMouseX = e.clientX;
      instance._tooltipMouseY = e.clientY;
    };

    const handleLeaveAll = () => {
      instance._lastActiveTs = 0;
      forceClose(instance);
      instance._tooltipMouseX = undefined;
      instance._tooltipMouseY = undefined;
    };

    const handleCanvasLeave = () => {
      instance._lastActiveTs = 0;
      instance.setActiveElements([]);
      instance._tooltipFrozen = false;
      if (!tooltipLockedRef.current) {
        setPopperOpen(false);
        popperOpenRef.current = false;
      }
      instance._tooltipMouseX = undefined;
      instance._tooltipMouseY = undefined;
    };
    const handleCanvasClick = () => {
      instance._tooltipFrozen = !instance._tooltipFrozen;
    };

    canvas.addEventListener('mousemove', handleMouseMove, { passive: true });
    canvas.addEventListener('mouseleave', handleCanvasLeave, { passive: true });
    canvas.addEventListener('pointerleave', handleLeaveAll, { passive: true });
    canvas.addEventListener('touchend', handleLeaveAll, { passive: true });
    canvas.addEventListener('click', handleCanvasClick, { passive: true });

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleLeaveAll);
      canvas.removeEventListener('pointerleave', handleLeaveAll);
      canvas.removeEventListener('touchend', handleLeaveAll);
      canvas.removeEventListener('click', handleCanvasClick);

      if (externalPopperTooltip.raf) {
        cancelAnimationFrame(externalPopperTooltip.raf);
        externalPopperTooltip.raf = null;
      }

      clearTimeout(graceTimerRef.current);
      forceClose(instance);

      instance.destroy();
      chartInstanceRef.current = null;
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideScheduledRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invert, dualAxisSelection, chartData]);



    // Cuando cambian labels, refresca límites y zoom
    useEffect(() => {
      const chart = chartInstanceRef.current;
      if (!chart) return;

      const min = 0;
      const max = Math.max(0, (Array.isArray(chart.data.labels) ? chart.data.labels.length : 1) - 1);

      chart.options.plugins.zoom.limits.x.min = min;
      chart.options.plugins.zoom.limits.x.max = max;
      chart.options.scales.x.min = min;
      chart.options.scales.x.max = max;

      chart.resetZoom();
      chart.update('none');
    }, [invert, datos.labels?.length]);

    // ----------------- External handler -> Popper -----------------
    const externalPopperTooltip = useCallback((context) => {
      const { chart, tooltip } = context;
      // Guards por si el chart se destruye entre frames
      if (!chart || chart._destroyed || !chart.canvas) return;

      // gracia de “última vez activo”
      const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      if (tooltip.opacity > 0) chart._lastActiveTs = now;
      const withinGrace = chart._lastActiveTs ? (now - chart._lastActiveTs) < 280 : false;

        // === Programar cierre cuando NO visible o sin puntos ===
      if (tooltip.opacity === 0 || !(tooltip.dataPoints?.length)) {
        clearTimeout(graceTimerRef.current);
        if (!withinGrace) {
          graceTimerRef.current = setTimeout(() => forceClose(chart), 250);
        }
        return;
      }

        // visible → cancelar cierre pendiente
      clearTimeout(graceTimerRef.current);

      // No hay puntos
      if (!tooltip.dataPoints?.length) {
        if (!tooltipLockedRef.current) { setPopperOpen(false); popperOpenRef.current = false; }
        return;
      }

      // Si el canvas pide ocultar
      if (tooltip.opacity === 0 && !chart._tooltipFrozen && !tooltipLockedRef.current && !withinGrace) {
        if (!hideScheduledRef.current) {
          hideScheduledRef.current = true;
          hideTimerRef.current = setTimeout(() => {
            hideScheduledRef.current = false;
            if (!tooltipLockedRef.current) { setPopperOpen(false); popperOpenRef.current = false; }
            hideTimerRef.current = null;
          }, 280);
        }
        return;
      }

      // visible: cancelar cierre pendiente
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
        hideScheduledRef.current = false;
      }

      // ===== Anclaje al centro del canvas y elección de arriba/abajo =====
      // const rect = chart.canvas.getBoundingClientRect();
      // const centerX = rect.left + rect.width / 2;
      // const spaceAbove = rect.top;
      // const spaceBelow = window.innerHeight - rect.bottom;
      // const preferBottom = spaceBelow >= spaceAbove; // más espacio abajo ⇒ abajo
      // const anchorY = preferBottom ? rect.bottom + 8 : rect.top - 8; // 8px de separación
      // const pageX = centerX + window.pageXOffset;
      // const pageY = anchorY + window.pageYOffset;
      // virtualAnchorRef.current.getBoundingClientRect = () => ({
      //   x: pageX, y: pageY, left: pageX, right: pageX, top: pageY, bottom: pageY,
      //   width: 0, height: 0, toJSON: () => {}
      // });
      // setPopperPlacement(preferBottom ? 'bottom' : 'top');
      // ===== Posicionar el Popper sobre el mouse =====
      const rect = chart.canvas.getBoundingClientRect();

      // Coordenadas del caret del tooltip (posición X del cursor dentro del canvas)
      const caretX = tooltip.caretX;
      const caretY = tooltip.caretY;

      // Calcular posición absoluta (en la página)
      const pageX = rect.left + caretX + window.pageXOffset;

      // Determinar si hay más espacio arriba o abajo
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const preferBottom = spaceBelow >= spaceAbove;

      // Mantener altura estable: sobre o debajo del canvas
      const anchorY = preferBottom ? rect.bottom + 8 : rect.top - 8;
      const pageY = anchorY + window.pageYOffset;

      // Evitar que el tooltip se salga de la pantalla
      const safeX = Math.max(16, Math.min(pageX, window.innerWidth - 16));

      // Actualizar ancla virtual para Popper
      virtualAnchorRef.current.getBoundingClientRect = () => ({
        x: safeX,
        y: pageY,
        left: safeX,
        right: safeX,
        top: pageY,
        bottom: pageY,
        width: 0,
        height: 0,
        toJSON: () => {}
      });

      setPopperPlacement(preferBottom ? 'bottom' : 'top');
      
      if (!popperOpenRef.current) { setPopperOpen(true); popperOpenRef.current = true; }


      // ---- Construcción de contenido (idéntica a tu HTML previo) ----
      // Filtrar ceros y duplicados por datasetIndex
      const uniques = [];
      const seen = new Set();
      (tooltip.dataPoints || []).forEach(dp => {
        if ((dp.parsed?.y !== 0) && !seen.has(dp.datasetIndex)) {
          seen.add(dp.datasetIndex);
          uniques.push(dp);
        }
      });
      if (uniques.length === 0) {
        if (!tooltipLockedRef.current) { setPopperOpen(false); popperOpenRef.current = false; }
        return;
      }

      // Agrupar por eje
      const groups = uniques.reduce((acc, dp) => {
        const axisID = chart.data.datasets[dp.datasetIndex].yAxisID || 'y';
        (acc[axisID] ||= []).push(dp);
        return acc;
      }, {});
      const axisIDs = Object.keys(groups);

      // Título = etiqueta X
      const title = tooltip.title?.[0] || '';

      // Encabezados de ejes (limpia [..] si existe)
      const axisTitles = axisIDs.map(id => {
        const scaleOpts = chart.options.scales[id] || {};
        const titleArr = scaleOpts.title?.text;
        let axisTitle = Array.isArray(titleArr) ? titleArr.join(' ') : (titleArr || id);
        const match = axisTitle.match && axisTitle.match(/\[(.*?)\]/);
        if (match) axisTitle = match[1];
        return { id, title: axisTitle, color: (scaleOpts.ticks?.color || 'rgba(255,255,255,.8)') };
      });

      // Orden base
      const baseAxisId = axisIDs[0];
      const labelOrder = groups[baseAxisId].map(dp => (chart.data.datasets[dp.datasetIndex]?.label ?? ''));

      // Mapa etiqueta -> { [axisID]: { value, color } }
      const matrix = new Map();
      axisIDs.forEach(axisID => {
        groups[axisID].forEach(dp => {
          const ds = chart.data.datasets[dp.datasetIndex];
          const etiqueta = ds?.label ?? '';
          const val = dp.parsed?.y ?? '';
          let dsColor = ds.textColor || ds.borderColor || ds.backgroundColor || '#a1a1a1';
          if (Array.isArray(dsColor)) dsColor = dsColor[dp.dataIndex];
          if (!matrix.has(etiqueta)) matrix.set(etiqueta, {});
          matrix.get(etiqueta)[axisID] = { value: val, color: dsColor };
        });
      });

      const fechaRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      const isFecha = labelOrder.every(lbl => fechaRegex.test(lbl));

      setPopperData({
        title,
        axisTitles,
        rows: { order: labelOrder, matrix, isFecha }
      });
      if (!popperOpenRef.current) { setPopperOpen(true); popperOpenRef.current = true; }
    }, []);

    // ================== CHART MODAL ==================
    const createModalChart = () => {
      if (!expandedCanvasRef.current) return;
      // modalChartRef.current = new Chart(expandedCanvasRef.current, {
      //   type: 'bar', data: sortedDatos, options: barOptions
      // });
      const modalInstance = new Chart(expandedCanvasRef.current, {
        type: 'bar',
        data: sortedDatos,
        options: barOptions,
        plugins: [leftAxisPatternPlugin], // por consistencia
      });
      modalChartRef.current = modalInstance;

      // permite que las leyendas modales también funcionen
      chartInstanceRef.current = modalInstance;


    };

    const destroyModalChart = () => {
      if (modalChartRef.current) {
        modalChartRef.current.destroy();
        modalChartRef.current = null;
      }
    };

    useEffect(() => {
      if (modalChartRef.current) {
        modalChartRef.current.data = sortedDatos;
        modalChartRef.current.update('none');
      }
    }, [invert, dualAxisSelection]);

  return (
    <Grid item xs={6} style={{ position: "relative", width: "100%", height: "100%" ,}}>
      {loadingPDF && progressPDF > 0 && (
        <Box sx={{ width: '30%', mt: '.8vh',marginLeft:'26vw', position:'absolute' }}>
          <LinearProgress
            variant="determinate"
            value={progressPDF}
            // sx={{ height: 8, borderRadius: 2 }}
            sx={{
                height: '.8vh',
                borderRadius: '.5vw',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#e94e1a',   // 👈 color de la barra
                },
                backgroundColor: 'rgba(233,78,26,0.2)' // 👈 opcional, color del fondo
              }}
          />
          <p  sx={{ mt: '.25vh', textAlign: "center" }}>
            {progressPDF}% {t(translate, 'generating_pdf', 'Generando PDF...')}
          </p>
        </Box>
      )}
      <SwapVertIcon
        onClick={() => setInvert(!invert)}
        style={{ position: 'absolute', marginLeft: iconLeft, marginTop: '1vh', fontSize: '1.3vw', color: '#c3c3c3' }}
      />

      {/* === Grafico + Leyendas + titulo === */}
      <div className='card' style={cardStyles.large}>
              {/* === Opciones de descarga y ampliar grafico === */}
        <div style={{ position: "relative", width:'100%' }}>
         <SpeedDial
          ariaLabel="Opciones"
          icon={
            <MoreVertIcon
              sx={{
                color: '#c3c3c3 !important',
                fontSize: '1vw !important',
              }}
            />
          }
          direction="down"
          FabProps={{
            sx: {
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
              width: '2vw !important',
              height: '2vw !important',
              background: 'rgba(0, 0, 0, 0)',
              boxShadow: 0,
              '&:hover': { background: 'rgba(0,0,0,0)' },
              marginBottom: '-2vh',
            },
          }}
          sx={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
            right: '0vw',
            top: '.3vh',
            zIndex: 10,
            color: '#c3c3c3 !important',
            fontSize: '1vw !important',
          }}
        >
          <SpeedDialAction
            key="expand"
            icon={
              <OpenInFullIcon
                className="icon-m"
                sx={{ fontSize: '.8vw !important' }}
              />
            }
            tooltipTitle={t(translate, 'expand', 'Expandir')}
            onClick={() => setExpanded(true)}
            FabProps={{
              sx: {
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                background: 'rgba(0,0,0,0.95)',
                color: 'white',
                width: '1.5vw !important',
                height: '1.5vw !important',
                boxShadow: 'none',
                '&:hover': { background: 'rgba(0,0,0,0.8)' },
                marginBottom: '-.2vh',
              },
            }}
          />

          <SpeedDialAction
            key="download"
            icon={
              <FileDownloadIcon
                className="icon-m"
                sx={{ fontSize: '.8vw !important' }}
              />
            }
            tooltipTitle={t(translate, 'download', 'Descargar')}
            onClick={handleOpenDialog}
            FabProps={{
              sx: {
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                background: 'rgba(0,0,0,0.95)',
                color: 'white',
                width: '1.5vw !important',
                height: '1.5vw !important',
                boxShadow: 'none',
                '&:hover': { background: 'rgba(0,0,0,0.8)' },
              },
            }}
          />
        </SpeedDial>

          <ExportMenuGeneric
            chartInstanceRef={chartInstanceRef}
            invert={invert}
            jaulas={chartData}
            allLabels={sortedDatos.labels}
            toggleLabels={toggleLabels}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            setLoadingPDF={setLoadingPDF}
            setProgressPDF={setProgressPDF}
            translate={translate}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-start', width: '34.5vw', marginBottom: '0vh' }}>
          {toggleLabels.map(item => (
            <button className='h2'
              key={item.field}
              onContextMenu={(e) => handleContextMenu(e, item.field)}
              onClick={() => setDualAxisSelection(prev => ({ ...prev, [item.field]: !prev[item.field] }))}
              style={{
                marginRight: marginRight, marginLeft: marginLeft,
                color: dualAxisSelection[item.field] ? item.activeColor : item.inactiveColor,
                background:'transparent', border:0
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <Leyendas data={datos} toggle={toggleDatasetVisibility} visible={datasetsVisibility} translate={translate} />

        <div style={styleCardComparar}>
          <canvas ref={chartRef} />
        </div>
      </div>

      {/* === MODAL EXPANDIDO === */}
      <Dialog
        open={expanded}
        PaperProps={{
          sx: {
            bgcolor: '#0c0c0c !important', 
            color: '#e0e0e0',
            minWidth: '81.5vw !important',
            height: '64.5vh',
            top: '16.2vh',
            right: '8vw',
            borderRadius: '0.4vw',
            boxShadow: 'none',
          },
        }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0)' },
          },
        }}
        TransitionProps={{
          onEntered: () => createModalChart(),
          onExited: () => destroyModalChart(),
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            bgcolor: '#0c0c0c',
            color: '#e0e0e0',
          }}
        >
          <Box>
            <IconButton
              onClick={() => setInvert(!invert)}
              aria-label={t(translate, 'invert_axes', 'Invertir ejes')}
              sx={{ mr: 1, color: '#e0e0e0' }}
            >
              <SwapVertIcon
                className="icon-m"
                style={{
                  left: '1vw',
                  top: '.3vh',
                  fontSize: '1.3vw',
                  width: '1.2vw',
                  aspectRatio: 1,
                }}
              />
            </IconButton>
          </Box>

          <Box sx={{ mb: 2, width: '55vw' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignContent: 'flex-start',
                alignItems: 'flex-start',
                width: '87%',
                scale: '1.2',
                marginTop: '1.5vh',
                marginLeft: '5vw',
              }}
            >
              {toggleLabels.map((item) => (
                <h2
                  key={`${item.label}-${item.field}`}
                  onContextMenu={(e) => handleContextMenu(e, item.field)}
                  onClick={() =>
                    setDualAxisSelection((prev) => ({
                      ...prev,
                      [item.field]: !prev[item.field],
                    }))
                  }
                  style={{
                    marginLeft: '1vw',
                    color: dualAxisSelection[item.field]
                      ? item.activeColor
                      : item.inactiveColor,
                  }}
                >
                  {item.label}
                </h2>
              ))}
            </div>
          </Box>

          <DialogActions>
            <Button
              onClick={() => setExpanded(false)}
              startIcon={<CloseFullscreenIcon style={{ fontSize: '1vw' }} />}
              sx={{ color: '#e0e0e0', marginLeft:'20vw' }}
            />
          </DialogActions>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: '#0c0c0c',
            color: '#e0e0e0',
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Leyendas
              data={datos}
              toggle={toggleDatasetVisibilityModal}
              visible={datasetsVisibility}
              width={'75vw'}
              translate={translate}
            />
          </Box>

          <Box sx={expandedChartBoxStyle}>
            {expanded && <canvas ref={expandedCanvasRef} key="expanded" />}
          </Box>
        </DialogContent>
      </Dialog>


      {/* --- Popper anclado a posición del mouse (virtual anchor) --- */}
      <Popper
        open={popperOpen}
        anchorEl={virtualAnchorRef.current}
        // placement="top"
        placement={popperPlacement}
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } }, // sin gap para facilitar entrar con el mouse
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: '.5vw' } },
          { name: 'flip', enabled: true },
        ]}
        sx={{ zIndex: 1300, pointerEvents: 'auto' }}
      >
        <Box
          elevation={3}
          sx={{
            p: '0.5vw',
            maxHeight: '40vh',
            overflowY: 'auto',
            bgcolor: 'rgba(0,0,0,0.96)',
            color: '#a1a1a1',
            border: '0.01vw solid #e84e0e',
            borderRadius: '.5vw',
            minWidth: 260,
          }}
          onMouseEnter={() => {
            tooltipLockedRef.current = true;
            // clearTimeout(graceTimerRef.current);
            if (hideTimerRef.current) {
              // clearTimeout(hideTimerRef.current);
              hideTimerRef.current = null;
              hideScheduledRef.current = false;
            }
          }}
          onMouseLeave={() => {
            tooltipLockedRef.current = false;
            setPopperOpen(false);
            popperOpenRef.current = false;
            // forceClose(chartInstanceRef.current);
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          <TooltipPivot
            title={popperData.title}
            axisTitles={popperData.axisTitles}
            rows={popperData.rows}
            titulo={titulo}
            translate={translate}

          />
        </Box>
      </Popper>
    </Grid>
  );
};

export default BarChartGeneric;
